import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyIcon, PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { PageHeader } from '@/components/layout';
import {
  Button,
  Card,
  ErrorSummary,
  Input,
  LoadingSpinner,
  Modal,
  ModalFooter,
} from '@/components/common';
import { getErrorMessage } from '@/services/api';
import {
  insurerRolesService,
  type InsurerRole,
  type InsurerRoleCatalog,
} from '@/services/insurerRoles';

/**
 * Insurer admin > Roles (#397). Owner decision: fully custom roles -- the
 * admin names a role, ticks the individual permissions it carries, and assigns
 * it to members on the Team page.
 *
 * Backed by `/insurers/insurance/roles/` (`InsurerRoleViewSet`), scoped to the
 * admin's OWN insurer. The permission grid comes from the server's catalog, so
 * the boxes offered are exactly the codes the server accepts, and the server
 * enforces every one of them on its own endpoints.
 */

const ROLES_QUERY_KEY = ['insurer-roles'];
const CATALOG_QUERY_KEY = ['insurer-roles-catalog'];

function RoleEditor({
  role,
  catalog,
  onClose,
}: {
  role: InsurerRole | null;
  catalog: InsurerRoleCatalog;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [held, setHeld] = useState<Set<string>>(new Set(role?.permissions ?? []));
  const [errors, setErrors] = useState<string[]>([]);

  const save = useMutation({
    mutationFn: () => {
      const input = { name, description, permissions: Array.from(held) };
      return role ? insurerRolesService.update(role.id, input) : insurerRolesService.create(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
      toast.success(role ? 'Role saved' : 'Role created');
      onClose();
    },
    onError: (err) => setErrors([getErrorMessage(err)]),
  });

  const toggle = (module: string, level: string, on: boolean) => {
    setHeld((current) => {
      const next = new Set(current);
      const code = `${module}.${level}`;
      if (on) {
        next.add(code);
        // Any level needs the page itself; the server adds read too.
        next.add(`${module}.read`);
        // #401: deciding a claim is working it -- the server adds edit too.
        if (level === 'approve') next.add(`${module}.edit`);
      } else {
        next.delete(code);
        if (level === 'edit') next.delete(`${module}.approve`);
        if (level === 'read') {
          for (const other of Array.from(next))
            if (other.startsWith(`${module}.`)) next.delete(other);
        }
      }
      return next;
    });
  };

  return (
    <Modal isOpen onClose={onClose} title={role ? `Edit ${role.name}` : 'New Role'} size="lg">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          setErrors([]);
          if (!name.trim()) {
            setErrors(['Role name is required.']);
            return;
          }
          save.mutate();
        }}
      >
        <ErrorSummary messages={errors} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            id="role-name"
            label="Role Name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Input
            id="role-description"
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <table
          className="mt-4 min-w-full divide-y divide-gray-200 dark:divide-gray-700"
          aria-label="Permissions"
        >
          <thead>
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                Module
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                Permissions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {catalog.modules.map((module) => (
              <tr key={module.code}>
                <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                  {module.label}
                  {module.code === 'products' && (
                    <span className="block text-xs text-gray-500 dark:text-gray-400">
                      Edit also covers activating, deactivating, reverting and broker access.
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-4">
                    {module.levels.map((level) => {
                      const code = `${module.code}.${level.code}`;
                      return (
                        <label
                          key={code}
                          className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300"
                        >
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-primary-600 dark:border-gray-600"
                            aria-label={`${module.label}: ${level.label}`}
                            checked={held.has(code)}
                            onChange={(e) => toggle(module.code, level.code, e.target.checked)}
                          />
                          {level.label}
                        </label>
                      );
                    })}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          Members with this role get exactly these permissions. Admins always have every permission.
        </p>
        <ModalFooter>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" isLoading={save.isPending}>
            {role ? 'Save Role' : 'Create Role'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export function RolesPage() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<InsurerRole | 'new' | null>(null);
  const [deleting, setDeleting] = useState<InsurerRole | null>(null);

  const rolesQuery = useQuery({
    queryKey: ROLES_QUERY_KEY,
    queryFn: () => insurerRolesService.list(),
  });
  const catalogQuery = useQuery({
    queryKey: CATALOG_QUERY_KEY,
    queryFn: () => insurerRolesService.catalog(),
    staleTime: 1000 * 60 * 60,
  });

  const deleteMutation = useMutation({
    mutationFn: (role: InsurerRole) => insurerRolesService.remove(role.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ROLES_QUERY_KEY });
      toast.success('Role deleted');
    },
    onError: (err) => toast.error(getErrorMessage(err)),
    onSettled: () => setDeleting(null),
  });

  const labels = new Map<string, string>();
  for (const module of catalogQuery.data?.modules ?? []) {
    for (const level of module.levels)
      labels.set(`${module.code}.${level.code}`, `${module.label}: ${level.label}`);
  }
  const roles = rolesQuery.data ?? [];
  const loading = rolesQuery.isLoading || catalogQuery.isLoading;
  const failed = rolesQuery.isError || catalogQuery.isError;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles"
        subtitle="Custom roles for your team. Assign them on the Team page."
        actions={
          <Button
            onClick={() => setEditing('new')}
            disabled={!catalogQuery.data}
            leftIcon={<PlusIcon className="h-4 w-4" />}
          >
            New Role
          </Button>
        }
      />

      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : failed ? (
          <div className="py-12 text-center" role="alert">
            <p className="text-sm text-red-600 dark:text-red-400">
              Could not load roles: {getErrorMessage(rolesQuery.error ?? catalogQuery.error)}
            </p>
            <Button
              variant="outline"
              className="mt-3"
              onClick={() => {
                rolesQuery.refetch();
                catalogQuery.refetch();
              }}
            >
              Retry
            </Button>
          </div>
        ) : roles.length === 0 ? (
          <div className="py-12 text-center">
            <KeyIcon className="mx-auto h-10 w-10 text-gray-400 dark:text-gray-500" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              No custom roles yet. Members without one have standard access (dashboard, policies and
              claims, view only).
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Role
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Permissions
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Members
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
                {roles.map((role) => (
                  <tr key={role.id} data-testid={`role-row-${role.id}`}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                      <div className="font-medium">{role.name}</div>
                      {role.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {role.description}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                      {role.permissions.length === 0
                        ? 'No permissions'
                        : role.permissions.map((code) => labels.get(code) ?? code).join(', ')}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {role.member_count}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => setEditing(role)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleting(role)}>
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editing && catalogQuery.data && (
        <RoleEditor
          role={editing === 'new' ? null : editing}
          catalog={catalogQuery.data}
          onClose={() => setEditing(null)}
        />
      )}
      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Delete this role?"
        description={
          deleting
            ? deleting.member_count > 0
              ? `${deleting.name} is assigned to ${deleting.member_count} member(s). Give them another role on the Team page first.`
              : `${deleting.name} will be deleted.`
            : undefined
        }
      >
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleting(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            isLoading={deleteMutation.isPending}
            disabled={!!deleting && deleting.member_count > 0}
            onClick={() => deleting && deleteMutation.mutate(deleting)}
          >
            Delete
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default RolesPage;
